import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../home/components/Footer';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { mockDiscussions } from '../../mocks/discussions';
import AvatarDisplay from '../../components/feature/AvatarDisplay';
import { MessagesModal } from '../../components/feature/MessagesModal';
import ReputationLevelBadge from '../../components/base/ReputationLevelBadge';
import UserBadge from '../../components/base/UserBadge';
import VerifiedCreatorBadge from '../../components/base/VerifiedCreatorBadge';
import { useReputation } from '../../hooks/useReputation';
import { useReputationRewards } from '../../hooks/useReputationRewards';
import BadgeShowcase from '../profile/components/BadgeShowcase';
import { createDiscussionUrl } from '../../hooks/useNavigation';

interface UserProfile {
  id: string;
  username: string;
  bio: string | null;
  avatar_id: string | null;
  avatar_url: string | null;
  reputation: number;
  followers_count: number;
  following_count: number;
  created_at: string;
  is_verified_creator?: boolean;
  is_bot?: boolean;
  slug?: string;
}

interface UserPost {
  id: string;
  title: string;
  content: string;
  category: string;
  categoryName: string;
  upvotes: number;
  commentCount: number;
  createdAt: string;
  tags: string[];
  slug?: string;
}

interface UserActivity {
  id: string;
  type: 'post' | 'comment' | 'vote';
  description: string;
  targetTitle?: string;
  targetUrl?: string;
  createdAt: string;
}

const categoryConfig: Record<string, { label: string; color: string; bgColor: string }> = {
  general: { label: 'General Discussion', color: 'text-blue-600', bgColor: 'bg-blue-500' },
  reviews: { label: 'Drama Reviews', color: 'text-yellow-600', bgColor: 'bg-yellow-500' },
  creators: { label: 'Creator Spotlight', color: 'text-rose-600', bgColor: 'bg-rose-500' },
  recommendations: { label: 'Recommendations', color: 'text-green-600', bgColor: 'bg-green-500' },
  news: { label: 'Industry News', color: 'text-red-600', bgColor: 'bg-red-500' },
  'behind-scenes': { label: 'Behind the Scenes', color: 'text-teal-600', bgColor: 'bg-teal-500' },
  'ai-dramas': { label: 'AI Dramas', color: 'text-orange-600', bgColor: 'bg-orange-500' },
};

export default function UserProfilePage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  
  const [resolvedUserId, setResolvedUserId] = useState<string | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [posts, setPosts] = useState<UserPost[]>([]);
  const [activities, setActivities] = useState<UserActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'posts' | 'badges' | 'activity'>('posts');
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [stats, setStats] = useState({ posts: 0, comments: 0 });
  const [showMessagesModal, setShowMessagesModal] = useState(false);

  const { unlockedBadges, lockedBadges, level, nextLevel, progress, totalBadges } = useReputation(resolvedUserId || undefined);
  const { awardPoints } = useReputationRewards();

  useEffect(() => {
    if (userId) {
      fetchUserProfile();
    }
  }, [userId]);

  const fetchUserProfile = async () => {
    setLoading(true);

    // 1. Try bot_profiles by slug first (username-based URL like /user/amira-hassan)
    const { data: botBySlug } = await supabase
      .from('bot_profiles')
      .select('*')
      .eq('slug', userId)
      .maybeSingle();

    if (botBySlug) {
      loadBotProfile(botBySlug);
      return;
    }

    // 2. Try bot_profiles by id (legacy URL like /user/user-001)
    const { data: botById } = await supabase
      .from('bot_profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle();

    if (botById) {
      // Redirect to slug-based URL
      if (botById.slug) {
        navigate(`/user/${botById.slug}`, { replace: true });
        return;
      }
      loadBotProfile(botById);
      return;
    }

    // 3. Try mock discussions author by id
    const mockUser = mockDiscussions.find(d => d.author.id === userId);
    if (mockUser) {
      loadMockProfile(mockUser);
      return;
    }

    // 4. Try real user_profiles by id (UUID)
    try {
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (!profileError && profileData) {
        loadRealProfile(profileData);
        return;
      }

      // 5. Try real user_profiles by username
      const { data: profileByUsername } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('username', userId)
        .maybeSingle();

      if (profileByUsername) {
        loadRealProfile(profileByUsername);
        return;
      }
    } catch (err) {
      console.error('Error fetching user profile:', err);
    }

    setLoading(false);
  };

  const loadBotProfile = (bot: any) => {
    const botProfile: UserProfile = {
      id: bot.id,
      username: bot.username,
      bio: bot.bio,
      avatar_id: null,
      avatar_url: bot.avatar_url,
      reputation: bot.reputation || 0,
      followers_count: bot.followers_count || 0,
      following_count: bot.following_count || 0,
      created_at: bot.created_at || new Date(Date.now() - 180 * 24 * 60 * 60 * 1000).toISOString(),
      is_verified_creator: false,
      is_bot: true,
      slug: bot.slug,
    };
    setProfile(botProfile);
    setResolvedUserId(bot.id);

    // Get bot user's posts from mock data
    const userPosts = mockDiscussions
      .filter(d => d.author.id === bot.id)
      .map(d => ({
        id: d.id,
        title: d.title,
        content: d.content,
        category: d.category,
        categoryName: d.categoryName,
        upvotes: d.upvotes,
        commentCount: d.commentCount,
        createdAt: d.createdAt,
        tags: d.tags,
        slug: d.slug,
      }));
    setPosts(userPosts);
    setStats({ posts: userPosts.length, comments: Math.floor(Math.random() * 50) + 10 });

    // Generate activities from posts
    const botActivities: UserActivity[] = userPosts.slice(0, 5).map((p, i) => ({
      id: `act-${i}`,
      type: 'post' as const,
      description: 'Created a new post',
      targetTitle: p.title,
      targetUrl: createDiscussionUrl(p.category, p.id, p.title),
      createdAt: p.createdAt,
    }));
    setActivities(botActivities);
    setLoading(false);
  };

  const loadMockProfile = (mockUser: any) => {
    const mockProfile: UserProfile = {
      id: mockUser.author.id,
      username: mockUser.author.username,
      bio: 'Passionate about Arab dramas and storytelling. Active community member sharing insights and reviews.',
      avatar_id: null,
      avatar_url: mockUser.author.avatar,
      reputation: mockUser.author.reputation,
      followers_count: Math.floor(Math.random() * 500) + 50,
      following_count: Math.floor(Math.random() * 200) + 20,
      created_at: new Date(Date.now() - Math.random() * 365 * 24 * 60 * 60 * 1000).toISOString(),
      is_verified_creator: false,
      is_bot: false,
    };
    setProfile(mockProfile);
    setResolvedUserId(mockUser.author.id);

    const userPosts = mockDiscussions
      .filter(d => d.author.id === userId)
      .map(d => ({
        id: d.id,
        title: d.title,
        content: d.content,
        category: d.category,
        categoryName: d.categoryName,
        upvotes: d.upvotes,
        commentCount: d.commentCount,
        createdAt: d.createdAt,
        tags: d.tags,
        slug: d.slug,
      }));
    setPosts(userPosts);
    setStats({ posts: userPosts.length, comments: Math.floor(Math.random() * 50) + 10 });

    const mockActivities: UserActivity[] = [
      {
        id: 'act-1',
        type: 'post',
        description: 'Created a new post',
        targetTitle: userPosts[0]?.title,
        targetUrl: userPosts[0] ? createDiscussionUrl(userPosts[0].category, userPosts[0].id, userPosts[0].title) : undefined,
        createdAt: userPosts[0]?.createdAt || new Date().toISOString(),
      },
      {
        id: 'act-2',
        type: 'comment',
        description: 'Commented on a discussion',
        targetTitle: 'Best drama of 2024 so far?',
        createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      },
      {
        id: 'act-3',
        type: 'vote',
        description: 'Upvoted a post',
        targetTitle: 'Desert Winds Season Finale Review',
        createdAt: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
      },
    ];
    setActivities(mockActivities);
    setLoading(false);
  };

  const loadRealProfile = async (profileData: any) => {
    setProfile({
      id: profileData.id,
      username: profileData.username || 'Unknown User',
      bio: profileData.bio,
      avatar_id: profileData.avatar_id,
      avatar_url: profileData.avatar_url,
      reputation: profileData.reputation || 0,
      followers_count: profileData.followers_count || 0,
      following_count: profileData.following_count || 0,
      created_at: profileData.created_at,
      is_verified_creator: profileData.is_verified_creator || false,
      is_bot: profileData.is_bot || false,
    });
    setResolvedUserId(profileData.id);

    // Fetch user's posts
    const { data: postsData } = await supabase
      .from('posts')
      .select('*, discussion_categories(name)')
      .eq('author_id', profileData.id)
      .order('created_at', { ascending: false })
      .limit(20);

    if (postsData) {
      const mappedPosts = postsData.map((p: any) => ({
        id: p.id,
        title: p.title,
        content: p.content || '',
        category: 'general',
        categoryName: p.discussion_categories?.name || 'General',
        upvotes: p.upvotes || 0,
        commentCount: p.comment_count || 0,
        createdAt: p.created_at,
        tags: p.tags || [],
      }));
      setPosts(mappedPosts);
    }

    // Fetch stats
    const [postsCount, commentsCount] = await Promise.all([
      supabase.from('posts').select('id', { count: 'exact', head: true }).eq('author_id', profileData.id),
      supabase.from('comments').select('id', { count: 'exact', head: true }).eq('author_id', profileData.id),
    ]);
    setStats({
      posts: postsCount.count || 0,
      comments: commentsCount.count || 0,
    });

    // Check if current user is following
    if (currentUser) {
      const { data: followData } = await supabase
        .from('user_follows')
        .select('id')
        .eq('follower_id', currentUser.id)
        .eq('following_id', profileData.id)
        .maybeSingle();
      setIsFollowing(!!followData);
    }

    // Fetch activities
    const { data: activitiesData } = await supabase
      .from('user_activities')
      .select('*')
      .eq('user_id', profileData.id)
      .order('created_at', { ascending: false })
      .limit(20);

    if (activitiesData) {
      const mappedActivities = activitiesData.map((a: any) => ({
        id: a.id,
        type: a.activity_type as 'post' | 'comment' | 'vote',
        description: a.description || getActivityDescription(a.activity_type),
        targetTitle: a.target_title,
        targetUrl: a.target_url,
        createdAt: a.created_at,
      }));
      setActivities(mappedActivities);
    }

    setLoading(false);
  };

  const getActivityDescription = (type: string) => {
    switch (type) {
      case 'post': return 'Created a new post';
      case 'comment': return 'Left a comment';
      case 'vote': return 'Voted on a post';
      default: return 'Activity';
    }
  };

  const handleFollow = async () => {
    if (!currentUser) return;
    if (currentUser.id === resolvedUserId) return;
    
    setFollowLoading(true);
    try {
      if (isFollowing) {
        await supabase
          .from('user_follows')
          .delete()
          .eq('follower_id', currentUser.id)
          .eq('following_id', resolvedUserId);
        setIsFollowing(false);
        if (profile) {
          setProfile({ ...profile, followers_count: Math.max(0, profile.followers_count - 1) });
        }
      } else {
        await supabase
          .from('user_follows')
          .insert({ follower_id: currentUser.id, following_id: resolvedUserId });
        setIsFollowing(true);
        if (profile) {
          setProfile({ ...profile, followers_count: profile.followers_count + 1 });
        }
        
        if (resolvedUserId) {
          const result = await awardPoints(resolvedUserId, 'gain_follower');
          if (result.success) {
            const notification = document.createElement('div');
            notification.className = 'fixed bottom-4 right-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-4 py-2 rounded-lg shadow-lg z-50 transition-all duration-300';
            notification.innerHTML = `<i class="ri-user-follow-fill mr-2"></i>Following ${profile?.username}!`;
            document.body.appendChild(notification);
            setTimeout(() => {
              notification.classList.add('opacity-0', 'translate-y-2');
              setTimeout(() => notification.remove(), 300);
            }, 2000);
          }
        }
      }
    } catch (err) {
      console.error('Error toggling follow:', err);
    } finally {
      setFollowLoading(false);
    }
  };

  const handleSendMessage = () => {
    if (!currentUser) return;
    setShowMessagesModal(true);
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-5xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-6">
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <div className="h-32 bg-gray-200"></div>
              <div className="px-8 pb-6 -mt-12">
                <div className="flex items-end space-x-5">
                  <div className="w-28 h-28 bg-gray-300 rounded-2xl border-4 border-white"></div>
                  <div className="pb-2 space-y-2">
                    <div className="h-6 bg-gray-200 rounded w-40"></div>
                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-5xl mx-auto px-4 py-16 text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gray-100 flex items-center justify-center">
            <i className="ri-user-unfollow-line text-3xl text-gray-400"></i>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">User Not Found</h1>
          <p className="text-gray-600 mb-6">This user doesn&apos;t exist or their profile is private.</p>
          <Link to="/forum" className="inline-flex items-center text-amber-600 hover:text-amber-700 font-medium cursor-pointer">
            <i className="ri-arrow-left-line mr-2"></i>
            Back to Forum
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const isOwnProfile = currentUser?.id === resolvedUserId;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Profile Header Card */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden mb-6">
          {/* Cover Image */}
          <div className={`h-32 relative bg-gradient-to-r from-amber-400 via-orange-400 to-amber-500`}>
            <div className="absolute inset-0 bg-black/10"></div>
          </div>

          {/* Profile Info */}
          <div className="px-8 pb-6">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between -mt-12 relative z-10">
              <div className="flex items-end space-x-5">
                <div className="w-28 h-28 rounded-2xl border-4 border-white shadow-lg overflow-hidden bg-white relative">
                  {profile.avatar_url ? (
                    <img
                      src={profile.avatar_url}
                      alt={profile.username}
                      className="w-full h-full object-cover object-top"
                    />
                  ) : (
                    <AvatarDisplay
                      avatarId={profile.avatar_id || undefined}
                      username={profile.username}
                      size="xl"
                      className="w-full h-full"
                    />
                  )}
                  {profile.is_verified_creator && (
                    <div className="absolute -bottom-1 -right-1 z-10">
                      <VerifiedCreatorBadge size="md" />
                    </div>
                  )}
                </div>

                <div className="pb-2">
                  <div className="flex items-center space-x-2 mb-1">
                    <h1 className="text-2xl font-bold text-gray-900">{profile.username}</h1>
                    {profile.is_verified_creator && (
                      <VerifiedCreatorBadge size="sm" showLabel />
                    )}
                    <ReputationLevelBadge points={profile.reputation} size="sm" />
                  </div>
                  <div className="flex items-center space-x-3 text-sm text-gray-500 mt-1">
                    <span className="flex items-center">
                      <i className="ri-star-fill text-amber-400 mr-1"></i>
                      {profile.reputation} reputation
                    </span>
                    <span>•</span>
                    <span>Joined {formatDate(profile.created_at)}</span>
                  </div>
                  {/* Featured badges row */}
                  {unlockedBadges.length > 0 && (
                    <div className="flex items-center space-x-1.5 mt-2">
                      {unlockedBadges.slice(0, 5).map((badge) => (
                        <UserBadge key={badge.id} badge={badge} size="sm" />
                      ))}
                      {unlockedBadges.length > 5 && (
                        <button
                          onClick={() => setActiveTab('badges')}
                          className="w-8 h-8 rounded-xl bg-gray-100 flex items-center justify-center text-xs text-gray-500 hover:bg-gray-200 transition-colors cursor-pointer"
                        >
                          +{unlockedBadges.length - 5}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-4 md:mt-0 flex items-center space-x-3">
                {isOwnProfile ? (
                  <Link
                    to="/profile"
                    className="inline-flex items-center px-4 py-2 border border-amber-200 text-amber-700 rounded-lg hover:bg-amber-50 transition-colors cursor-pointer whitespace-nowrap text-sm font-medium"
                  >
                    <i className="ri-edit-line mr-2"></i>
                    Edit Profile
                  </Link>
                ) : currentUser ? (
                  <>
                    {!profile.is_bot && (
                      <button
                        onClick={handleSendMessage}
                        className="inline-flex items-center px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap text-sm font-medium"
                      >
                        <i className="ri-message-3-line mr-2"></i>
                        Message
                      </button>
                    )}
                    <button
                      onClick={handleFollow}
                      disabled={followLoading}
                      className={`inline-flex items-center px-5 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-sm font-medium ${
                        isFollowing
                          ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          : 'bg-amber-500 text-white hover:bg-amber-600'
                      }`}
                    >
                      {followLoading ? (
                        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2"></div>
                      ) : (
                        <i className={`${isFollowing ? 'ri-user-unfollow-line' : 'ri-user-add-line'} mr-2`}></i>
                      )}
                      {isFollowing ? 'Following' : 'Follow'}
                    </button>
                  </>
                ) : !profile.is_bot ? (
                  <button
                    onClick={handleSendMessage}
                    className="inline-flex items-center px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap text-sm font-medium"
                  >
                    <i className="ri-message-3-line mr-2"></i>
                    Message
                  </button>
                ) : null}
              </div>
            </div>

            {/* Bio */}
            {profile.bio && (
              <p className="text-gray-600 mt-4 max-w-2xl text-sm leading-relaxed">{profile.bio}</p>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          {[
            { label: 'Posts', value: stats.posts, icon: 'ri-article-line', color: 'from-amber-400 to-orange-400' },
            { label: 'Comments', value: stats.comments, icon: 'ri-chat-3-line', color: 'from-emerald-400 to-teal-400' },
            { label: 'Followers', value: profile.followers_count, icon: 'ri-user-heart-line', color: 'from-pink-400 to-rose-400' },
            { label: 'Following', value: profile.following_count, icon: 'ri-user-follow-line', color: 'from-cyan-400 to-sky-400' },
            { label: 'Badges', value: unlockedBadges.length, icon: 'ri-trophy-line', color: 'from-amber-400 to-yellow-400' },
          ].map((stat) => (
            <div key={stat.label} className="bg-white rounded-xl shadow-sm p-4 hover:shadow-md transition-shadow cursor-pointer" onClick={() => stat.label === 'Badges' ? setActiveTab('badges') : undefined}>
              <div className="flex items-center space-x-3">
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${stat.color} flex items-center justify-center`}>
                  <i className={`${stat.icon} text-white text-lg`}></i>
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                  <p className="text-xs text-gray-500">{stat.label}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="border-b border-gray-100">
            <div className="flex space-x-1 p-1">
              {[
                { id: 'posts', label: 'Posts', icon: 'ri-article-line', count: stats.posts },
                { id: 'badges', label: 'Badges', icon: 'ri-trophy-line', count: unlockedBadges.length },
                { id: 'activity', label: 'Activity', icon: 'ri-history-line' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex-1 px-4 py-3 rounded-lg text-sm font-medium transition-all cursor-pointer ${
                    activeTab === tab.id
                      ? 'bg-amber-50 text-amber-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <i className={`${tab.icon} mr-2`}></i>
                  {tab.label}
                  {tab.count !== undefined && (
                    <span className="ml-2 px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="p-6">
            {/* Posts Tab */}
            {activeTab === 'posts' && (
              <div className="space-y-4">
                {posts.length > 0 ? (
                  posts.map((post) => {
                    const config = categoryConfig[post.category] || categoryConfig.general;
                    const postUrl = createDiscussionUrl(post.category, post.id, post.title);
                    
                    return (
                      <Link
                        key={post.id}
                        to={postUrl}
                        className="block bg-gray-50 rounded-xl p-5 hover:bg-gray-100 transition-colors cursor-pointer group"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-2">
                              <span className={`px-2 py-0.5 ${config.bgColor} text-white text-xs rounded-full font-medium`}>
                                {config.label}
                              </span>
                              <span className="text-xs text-gray-400">{formatTimeAgo(post.createdAt)}</span>
                            </div>
                            <h3 className="text-base font-semibold text-gray-900 group-hover:text-amber-600 transition-colors line-clamp-2 mb-2">
                              {post.title}
                            </h3>
                            <p className="text-sm text-gray-600 line-clamp-2 mb-3">{post.content}</p>
                            <div className="flex items-center space-x-4 text-xs text-gray-500">
                              <span className="flex items-center">
                                <i className="ri-arrow-up-line mr-1"></i>
                                {post.upvotes} upvotes
                              </span>
                              <span className="flex items-center">
                                <i className="ri-message-3-line mr-1"></i>
                                {post.commentCount} comments
                              </span>
                            </div>
                          </div>
                          <i className="ri-arrow-right-s-line text-gray-400 group-hover:text-amber-500 text-xl ml-4 flex-shrink-0"></i>
                        </div>
                      </Link>
                    );
                  })
                ) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                      <i className="ri-article-line text-2xl text-gray-400"></i>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-1">No Posts Yet</h3>
                    <p className="text-gray-500 text-sm">
                      {isOwnProfile
                        ? "You haven't created any posts yet. Start sharing your thoughts!"
                        : "This user hasn't created any posts yet."}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Badges Tab */}
            {activeTab === 'badges' && (
              <BadgeShowcase
                unlockedBadges={unlockedBadges}
                lockedBadges={lockedBadges}
                level={level}
                nextLevel={nextLevel}
                progress={progress}
                reputation={profile.reputation}
                totalBadges={totalBadges}
              />
            )}

            {/* Activity Tab */}
            {activeTab === 'activity' && (
              <div className="space-y-4">
                {activities.length > 0 ? (
                  activities.map((activity) => (
                    <div
                      key={activity.id}
                      className="flex items-start space-x-4 p-4 bg-gray-50 rounded-xl"
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        activity.type === 'post'
                          ? 'bg-amber-100 text-amber-600'
                          : activity.type === 'comment'
                          ? 'bg-emerald-100 text-emerald-600'
                          : 'bg-blue-100 text-blue-600'
                      }`}>
                        <i className={`${
                          activity.type === 'post'
                            ? 'ri-article-line'
                            : activity.type === 'comment'
                            ? 'ri-chat-3-line'
                            : 'ri-arrow-up-line'
                        }`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900">
                          <span className="font-medium">{activity.description}</span>
                          {activity.targetTitle && (
                            <>
                              {' '}
                              {activity.targetUrl ? (
                                <Link
                                  to={activity.targetUrl}
                                  className="text-amber-600 hover:text-amber-700 hover:underline cursor-pointer"
                                >
                                  {activity.targetTitle}
                                </Link>
                              ) : (
                                <span className="text-gray-600">&quot;{activity.targetTitle}&quot;</span>
                              )}
                            </>
                          )}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">{formatTimeAgo(activity.createdAt)}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                      <i className="ri-history-line text-2xl text-gray-400"></i>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-1">No Activity Yet</h3>
                    <p className="text-gray-500 text-sm">
                      {isOwnProfile
                        ? 'Your recent activity will appear here.'
                        : "This user's activity will appear here."}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />

      {/* Messages Modal */}
      <MessagesModal
        isOpen={showMessagesModal}
        onClose={() => setShowMessagesModal(false)}
        startConversationWith={resolvedUserId || undefined}
      />
    </div>
  );
}
